import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";
import { api, programsApi } from "@/services/api";
import { formatDisplayDate, parseDateInput } from "@/utils/formatDisplayDate";
import { usePabSession } from "@/hooks/usePabSession";

/**
 * Computes the edit access end date (wrapupDate + 10 days) from a raw wrapupDate string.
 * Returns null if the wrapupDate is absent or invalid.
 */
export function computeEditAccessEndDate(
  wrapupDate: string | null | undefined,
): Date | null {
  const parsed = parseDateInput(wrapupDate);
  if (!parsed) return null;
  return addDays(parsed, 10);
}

/**
 * Returns true when today >= the given edit access end date,
 * meaning the edit window has closed.
 */
export function checkEditAccessExpired(endDate: Date | null): boolean {
  if (!endDate) return false;
  return startOfDay(new Date()) >= startOfDay(endDate);
}

/**
 * Hook for the achiever flow.
 * Reads the active PAB session's weekly plan (cached) and derives:
 *   - isEditAccessExpired  – true once today >= wrapupDate + 10 days
 *   - editAccessEndDateFormatted – human-readable end date (e.g. "Jun 15, 2026")
 *   - editAccessEndDate    – raw Date object (or null)
 */
export const useEditAccess = () => {
  const { session } = usePabSession();

  const { data: weeklyPlanData } = useQuery({
    queryKey: ["pab-session", "weekly-plan", session?.id],
    queryFn: () => api.pabSessions.getWeeklyPlan(session!.id, 12),
    // A board with no scheduled sessions has no starts_on/ends_on, and
    // /weekly-plan answers 400 "Session active dates are not set." rather than
    // an empty plan -- so there is nothing to ask for until they exist.
    enabled: !!session?.id && !!session?.activeFrom && !!session?.activeTill,
    staleTime: 0,
  });

  const editAccessEndDate = useMemo(
    () => computeEditAccessEndDate(weeklyPlanData?.wrapupDate),
    [weeklyPlanData?.wrapupDate],
  );

  const editAccessEndDateFormatted = useMemo(
    () => (editAccessEndDate ? formatDisplayDate(editAccessEndDate, { variant: "date" }) : null),
    [editAccessEndDate],
  );

  const isEditAccessExpired = useMemo(
    () => checkEditAccessExpired(editAccessEndDate),
    [editAccessEndDate],
  );

  return { isEditAccessExpired, editAccessEndDateFormatted, editAccessEndDate };
};

/**
 * Same as useEditAccess but for Expert Programs.
 * Reads the program's weekly plan (wrapupDate) and derives edit access state.
 */
export const useProgramEditAccess = (externalId: string | null | undefined) => {
  const { data: weeklyPlanData } = useQuery({
    queryKey: ["program-weekly-plan", externalId],
    queryFn: () => programsApi.getWeeklyPlan(externalId!),
    enabled: !!externalId,
    staleTime: 0,
  });

  const editAccessEndDate = useMemo(
    () => computeEditAccessEndDate(weeklyPlanData?.wrapupDate),
    [weeklyPlanData?.wrapupDate],
  );

  const editAccessEndDateFormatted = useMemo(
    () => (editAccessEndDate ? formatDisplayDate(editAccessEndDate, { variant: "date" }) : null),
    [editAccessEndDate],
  );

  const isEditAccessExpired = useMemo(
    () => checkEditAccessExpired(editAccessEndDate),
    [editAccessEndDate],
  );

  return { isEditAccessExpired, editAccessEndDateFormatted, editAccessEndDate };
};
